import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ObjectivesManager = ({ onObjectivesUpdate }) => {
  const [objectives, setObjectives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingObjective, setEditingObjective] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterPeriod, setFilterPeriod] = useState('');
  const [objectiveForm, setObjectiveForm] = useState({
    title: '',
    description: '',
    target_value: '',
    unit: 'FCFA',
    category: 'revenue',
    period: 'monthly',
    deadline: ''
  });

  const categories = [
    { value: 'revenue', label: 'Chiffre d\'affaires', icon: '💰' },
    { value: 'operations', label: 'Opérations', icon: '📦' },
    { value: 'operations_average', label: 'Moyenne Opérations', icon: '📊' },
    { value: 'clients_converted', label: 'Clients Convertis', icon: '🎯' },
    { value: 'opportunities', label: 'Opportunités', icon: '🚀' },
    { value: 'satisfaction', label: 'Satisfaction Client', icon: '😊' },
    { value: 'cost_reduction', label: 'Réduction Coûts', icon: '📉' }
  ];

  const periods = [
    { value: 'daily', label: 'Quotidien', icon: '📅' },
    { value: 'monthly', label: 'Mensuel', icon: '📆' },
    { value: 'quarterly', label: 'Trimestriel', icon: '📋' },
    { value: 'yearly', label: 'Annuel', icon: '📊' }
  ];

  const units = [
    { value: 'FCFA', label: 'FCFA' },
    { value: 'quantity', label: 'Quantité' },
    { value: 'percentage', label: 'Pourcentage' },
    { value: 'days', label: 'Jours' },
    { value: 'hours', label: 'Heures' }
  ];

  useEffect(() => {
    fetchObjectives();
  }, []);

  const fetchObjectives = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/objectives`);
      setObjectives(response.data);
      
      // Notify parent component of objectives update
      if (onObjectivesUpdate) {
        onObjectivesUpdate(response.data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des objectifs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const formData = {
        ...objectiveForm,
        target_value: parseFloat(objectiveForm.target_value),
        deadline: objectiveForm.deadline ? new Date(objectiveForm.deadline).toISOString() : null
      };

      if (editingObjective) {
        await axios.put(`${API}/objectives/${editingObjective.id}`, formData);
        alert('Objectif modifié avec succès !');
      } else {
        await axios.post(`${API}/objectives`, formData);
        alert('Objectif créé avec succès !');
      }
      
      fetchObjectives();
      resetForm();
      setShowModal(false);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      alert('Erreur lors de la sauvegarde de l\'objectif');
    }
  };

  const handleEdit = (objective) => {
    setEditingObjective(objective);
    setObjectiveForm({
      title: objective.title,
      description: objective.description || '',
      target_value: objective.target_value.toString(),
      unit: objective.unit,
      category: objective.category,
      period: objective.period,
      deadline: objective.deadline ? new Date(objective.deadline).toISOString().split('T')[0] : ''
    });
    setShowModal(true);
  };

  const handleDelete = async (objectiveId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet objectif ?')) {
      try {
        await axios.delete(`${API}/objectives/${objectiveId}`);
        fetchObjectives();
        alert('Objectif supprimé avec succès !');
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        alert('Erreur lors de la suppression de l\'objectif');
      }
    }
  };

  const resetForm = () => {
    setObjectiveForm({
      title: '',
      description: '',
      target_value: '',
      unit: 'FCFA',
      category: 'revenue',
      period: 'monthly',
      deadline: ''
    });
    setEditingObjective(null);
  };

  const calculateProgress = (objective) => {
    if (!objective.current_value || objective.target_value === 0) return 0;
    return Math.min((objective.current_value / objective.target_value) * 100, 100);
  };

  const getProgressColor = (progress) => {
    if (progress >= 90) return 'bg-green-500';
    if (progress >= 70) return 'bg-blue-500';
    if (progress >= 50) return 'bg-yellow-500';
    if (progress >= 30) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const getStatusColor = (objective) => {
    const progress = calculateProgress(objective);
    const deadline = new Date(objective.deadline);
    const today = new Date();
    const daysUntilDeadline = Math.ceil((deadline - today) / (1000 * 60 * 60 * 24));
    
    if (progress >= 100) return { bg: 'bg-green-100', text: 'text-green-800', badge: 'Atteint' };
    if (daysUntilDeadline < 0) return { bg: 'bg-red-100', text: 'text-red-800', badge: 'Expiré' };
    if (daysUntilDeadline <= 7 && progress < 70) return { bg: 'bg-orange-100', text: 'text-orange-800', badge: 'Urgent' };
    if (progress >= 70) return { bg: 'bg-blue-100', text: 'text-blue-800', badge: 'En cours' };
    return { bg: 'bg-yellow-100', text: 'text-yellow-800', badge: 'À suivre' };
  };

  const filteredObjectives = objectives.filter(objective => {
    const matchesSearch = objective.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         objective.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !filterCategory || objective.category === filterCategory;
    const matchesPeriod = !filterPeriod || objective.period === filterPeriod;
    
    return matchesSearch && matchesCategory && matchesPeriod;
  });

  const formatValue = (value, unit) => {
    if (unit === 'FCFA') {
      return new Intl.NumberFormat('fr-FR').format(value) + ' FCFA';
    } else if (unit === 'percentage') {
      return value + '%';
    } else {
      return value + ' ' + unit;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="spinner mb-4"></div>
          <p className="text-gray-600">Chargement des objectifs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <span className="mr-3">🎯</span>
            Gestion des Objectifs
          </h1>
          <p className="text-gray-600 mt-1">
            Configurez et suivez tous vos objectifs business
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors flex items-center"
        >
          <span className="mr-2">➕</span>
          Nouvel Objectif
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Objectifs</p>
              <p className="text-2xl font-bold text-gray-900">{objectives.length}</p>
            </div>
            <div className="text-3xl">🎯</div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Atteints</p>
              <p className="text-2xl font-bold text-green-600">
                {objectives.filter(obj => calculateProgress(obj) >= 100).length}
              </p>
            </div>
            <div className="text-3xl">✅</div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">En cours</p>
              <p className="text-2xl font-bold text-blue-600">
                {objectives.filter(obj => {
                  const progress = calculateProgress(obj);
                  return progress > 0 && progress < 100;
                }).length}
              </p>
            </div>
            <div className="text-3xl">🚀</div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Taux Succès</p>
              <p className="text-2xl font-bold text-purple-600">
                {objectives.length > 0 
                  ? Math.round((objectives.filter(obj => calculateProgress(obj) >= 100).length / objectives.length) * 100)
                  : 0}%
              </p>
            </div>
            <div className="text-3xl">📊</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <input
              type="text"
              placeholder="Rechercher un objectif..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
          
          <div>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="">Toutes les catégories</option>
              {categories.map(cat => (
                <option key={cat.value} value={cat.value}>
                  {cat.icon} {cat.label}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <select
              value={filterPeriod}
              onChange={(e) => setFilterPeriod(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="">Toutes les périodes</option>
              {periods.map(period => (
                <option key={period.value} value={period.value}>
                  {period.icon} {period.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Objectives Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredObjectives.map(objective => {
          const progress = calculateProgress(objective);
          const status = getStatusColor(objective);
          const categoryInfo = categories.find(cat => cat.value === objective.category);
          const periodInfo = periods.find(p => p.value === objective.period);
          
          return (
            <div key={objective.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              {/* Header */}
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    <span className="text-2xl mr-2">{categoryInfo?.icon}</span>
                    <h3 className="text-lg font-semibold text-gray-900">{objective.title}</h3>
                  </div>
                  {objective.description && (
                    <p className="text-sm text-gray-600 mb-2">{objective.description}</p>
                  )}
                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                    <span className="flex items-center">
                      {periodInfo?.icon} {periodInfo?.label}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${status.bg} ${status.text}`}>
                      {status.badge}
                    </span>
                  </div>
                </div>
                
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEdit(objective)}
                    className="text-blue-600 hover:text-blue-800 p-1"
                    title="Modifier"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => handleDelete(objective.id)}
                    className="text-red-600 hover:text-red-800 p-1"
                    title="Supprimer"
                  >
                    🗑️
                  </button>
                </div>
              </div>

              {/* Progress */}
              <div className="mb-4">
                <div className="flex justify-between text-sm mb-1">
                  <span className="font-medium">Progression</span>
                  <span className="text-gray-600">{progress.toFixed(1)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${getProgressColor(progress)}`}
                    style={{ width: `${Math.min(progress, 100)}%` }}
                  ></div>
                </div>
              </div>

              {/* Values */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-600">Objectif</p>
                  <p className="font-semibold text-gray-900">
                    {formatValue(objective.target_value, objective.unit)}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">Actuel</p>
                  <p className="font-semibold text-gray-900">
                    {formatValue(objective.current_value || 0, objective.unit)}
                  </p>
                </div>
              </div>

              {/* Deadline */}
              {objective.deadline && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-xs text-gray-500">
                    Échéance: {new Date(objective.deadline).toLocaleDateString('fr-FR')}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {filteredObjectives.length === 0 && (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">🎯</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun objectif trouvé</h3>
          <p className="text-gray-600 mb-6">
            {searchTerm || filterCategory || filterPeriod
              ? 'Aucun objectif ne correspond à vos critères de recherche.'
              : 'Commencez par créer votre premier objectif.'}
          </p>
          <button
            onClick={() => setShowModal(true)}
            className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors"
          >
            Créer un objectif
          </button>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">
              {editingObjective ? 'Modifier l\'objectif' : 'Nouvel objectif'}
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Titre *
                  </label>
                  <input
                    type="text"
                    value={objectiveForm.title}
                    onChange={(e) => setObjectiveForm({...objectiveForm, title: e.target.value})}
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Ex: Atteindre 10M FCFA de CA mensuel"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={objectiveForm.description}
                    onChange={(e) => setObjectiveForm({...objectiveForm, description: e.target.value})}
                    rows="3"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Description détaillée de l'objectif..."
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Valeur cible *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={objectiveForm.target_value}
                    onChange={(e) => setObjectiveForm({...objectiveForm, target_value: e.target.value})}
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Ex: 10000000"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Unité *
                  </label>
                  <select
                    value={objectiveForm.unit}
                    onChange={(e) => setObjectiveForm({...objectiveForm, unit: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    {units.map(unit => (
                      <option key={unit.value} value={unit.value}>{unit.label}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Catégorie *
                  </label>
                  <select
                    value={objectiveForm.category}
                    onChange={(e) => setObjectiveForm({...objectiveForm, category: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>
                        {cat.icon} {cat.label}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Période *
                  </label>
                  <select
                    value={objectiveForm.period}
                    onChange={(e) => setObjectiveForm({...objectiveForm, period: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    {periods.map(period => (
                      <option key={period.value} value={period.value}>
                        {period.icon} {period.label}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date limite
                  </label>
                  <input
                    type="date"
                    value={objectiveForm.deadline}
                    onChange={(e) => setObjectiveForm({...objectiveForm, deadline: e.target.value})}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 transition-colors"
                >
                  {editingObjective ? 'Modifier' : 'Créer'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="flex-1 bg-gray-500 text-white py-3 px-4 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ObjectivesManager;